import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DEFAULT_GC_TIME } from '@/client/constants';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import { AckInfo } from '@/client/types/scheduler';
import {
  Failure,
  FailuresResponse,
  FleetResponse,
  OverviewResponse,
  QueueDetailResponse,
  QueuesResponse,
  RestartResult,
  RunsResponse,
  ScheduleResponse,
  TaskDetailResponse,
  TasksResponse,
  WorkerAuditRow,
  WorkerDetailResponse,
  WorkerEnv,
  WorkerIssue,
  WorkerWindow,
} from '@/client/types/workers';

const BASE = '/api/admin/v2/workers';

const qs = (params: Record<string, unknown>) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    search.set(key, String(value));
  });
  const out = search.toString();
  return out ? `?${out}` : '';
};

export const workersKeys = { all: ['workers-v2'] as const };

/** Admin v2 wraps payloads in `{ status, data }`; accept either shape. */
const unwrap = <T>(raw: unknown): T => {
  if (raw != null && typeof raw === 'object' && 'data' in raw && 'status' in raw) {
    return (raw as { data: T }).data;
  }
  return raw as T;
};

const id = encodeURIComponent;
// A task name has dots and no slashes; the server takes it as a path.
const taskPath = (name: string) => name.split('/').map(id).join('/');

/** One read. `live` ones (what is running, what is waiting) are asked for again every 15 s. */
const useRead = <T>(path: string, live = false, enabled = true) => {
  const request = useClientStore((s) => s.request);
  const fn = withErrorHandling(
    async (): Promise<unknown> => unwrap<unknown>(await request<unknown>(`${BASE}${path}`)),
    'Failed to load workers',
  ) as unknown as () => Promise<T>;
  return useQuery({
    queryKey: [...workersKeys.all, path],
    queryFn: fn,
    enabled,
    staleTime: live ? 10_000 : 60_000,
    refetchInterval: live ? 15_000 : false,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

export const useWorkersOverviewQuery = (env: WorkerEnv, window: WorkerWindow, enabled = true) =>
  useRead<OverviewResponse>(`/overview${qs({ env, window })}`, true, enabled);

export const useWorkersFleetQuery = (env: WorkerEnv, enabled = true) =>
  useRead<FleetResponse>(`/fleet${qs({ env })}`, true, enabled);

export const useWorkerQuery = (env: WorkerEnv, name: string, window: WorkerWindow) =>
  useRead<WorkerDetailResponse>(`/workers/${id(name)}${qs({ env, window })}`, true, !!name);

export const useWorkersQueuesQuery = (env: WorkerEnv, enabled = true) =>
  useRead<QueuesResponse>(`/queues${qs({ env })}`, true, enabled);

export const useWorkersQueueQuery = (env: WorkerEnv, name: string, window: WorkerWindow) =>
  useRead<QueueDetailResponse>(`/queues/${id(name)}${qs({ env, window })}`, true, !!name);

export const useWorkersTasksQuery = (env: WorkerEnv, window: WorkerWindow, enabled = true) =>
  useRead<TasksResponse>(`/tasks${qs({ env, window })}`, false, enabled);

export const useWorkersTaskQuery = (env: WorkerEnv, name: string, window: WorkerWindow) =>
  useRead<TaskDetailResponse>(`/tasks/${taskPath(name)}${qs({ env, window })}`, false, !!name);

export const useWorkersRunsQuery = (
  env: WorkerEnv,
  filter: { task?: string; status?: string; worker?: string },
  enabled = true,
) => useRead<RunsResponse>(`/runs${qs({ env, ...filter, limit: 200 })}`, true, enabled);

export const useWorkersScheduleQuery = (env: WorkerEnv, enabled = true) =>
  useRead<ScheduleResponse>(`/schedule${qs({ env })}`, true, enabled);

export const useWorkersFailuresQuery = (
  env: WorkerEnv,
  window: WorkerWindow,
  status: string,
  enabled = true,
) => useRead<FailuresResponse>(`/failures${qs({ env, window, status })}`, false, enabled);

export const useWorkersFailureGroupQuery = (
  env: WorkerEnv,
  window: WorkerWindow,
  sig: string,
  enabled = true,
) =>
  useRead<{ failures: Failure[] }>(
    `/failures/${id(sig)}${qs({ env, window })}`,
    false,
    enabled && !!sig,
  );

export const useWorkersIssuesQuery = (env: WorkerEnv, status: string, enabled = true) =>
  useRead<{ count: number; issues: WorkerIssue[] }>(
    `/issues${qs({ env, status })}`,
    true,
    enabled,
  );

export const useWorkersAuditQuery = (env: WorkerEnv, enabled = true) =>
  useRead<{ audit: WorkerAuditRow[] }>(`/audit${qs({ env, limit: 200 })}`, false, enabled);

export const useWorkersRefresh = () => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: workersKeys.all });
};

/** Every change the console can make to the workers, as one call. */
export type WorkerChange =
  | { kind: 'run_task'; task: string; args?: unknown[]; kwargs?: Record<string, unknown> }
  | { kind: 'run_entry'; entry: string }
  | { kind: 'revoke'; taskId: string; task?: string }
  | { kind: 'retry_failure'; failureId: string }
  | { kind: 'pause_queue' | 'resume_queue'; worker: string; queue: string }
  | { kind: 'restart'; component: string; worker: string }
  | { kind: 'rate_limit'; task: string; rate: string; worker?: string }
  | { kind: 'purge'; queue: string };

const build = (c: WorkerChange): { path: string; body: Record<string, unknown> } => {
  switch (c.kind) {
    case 'run_task':
      return { path: '/tasks/run', body: { task: c.task, args: c.args, kwargs: c.kwargs } };
    case 'run_entry':
      return { path: `/schedule/${id(c.entry)}/run`, body: {} };
    case 'revoke':
      return { path: '/tasks/revoke', body: { task_id: c.taskId, task: c.task } };
    case 'retry_failure':
      return { path: `/failures/${id(c.failureId)}/retry`, body: {} };
    case 'pause_queue':
    case 'resume_queue':
      return {
        path: `/workers/${id(c.worker)}/queues/${id(c.queue)}/${c.kind === 'pause_queue' ? 'pause' : 'resume'}`,
        body: {},
      };
    case 'restart':
      return { path: '/workers/restart', body: { component: c.component, worker: c.worker } };
    case 'rate_limit':
      return { path: '/tasks/rate-limit', body: { task: c.task, rate: c.rate, worker: c.worker } };
    case 'purge':
      return { path: `/queues/${id(c.queue)}/purge`, body: {} };
  }
};

export type ChangeResult = Partial<RestartResult> & {
  env: WorkerEnv;
  task_id?: string;
  removed?: number;
  queue?: string;
};

export const useWorkerChangeMutation = () => {
  const request = useClientStore((s) => s.request);
  const queryClient = useQueryClient();
  const fn = withErrorHandling(
    async (v: { env: WorkerEnv; reason: string; change: WorkerChange }): Promise<ChangeResult> => {
      const { path, body } = build(v.change);
      return unwrap<ChangeResult>(
        await request<unknown>(`${BASE}${path}`, 'POST', { ...body, env: v.env, reason: v.reason }),
      );
    },
    'The change was not made',
  );
  return useMutation({
    mutationFn: fn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: workersKeys.all }),
  });
};

export const useWorkerAckMutation = () => {
  const request = useClientStore((s) => s.request);
  const queryClient = useQueryClient();
  const fn = withErrorHandling(
    async (v: { issueId: string; note?: string; name?: string; env: WorkerEnv }) =>
      unwrap<{ id: string; ack: AckInfo }>(
        await request<unknown>(`${BASE}/issues/ack`, 'POST', {
          id: v.issueId,
          note: v.note,
          name: v.name,
          env: v.env,
        }),
      ),
    'Failed to acknowledge',
  );
  return useMutation({
    mutationFn: fn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: workersKeys.all }),
  });
};
